import { createSlice } from '@reduxjs/toolkit'
import { nanoid } from 'nanoid'

import { REDUCERS } from 'common/store/reducerConstants'

import type { PayloadAction } from '@reduxjs/toolkit'
import type { Vector3Like } from 'three'
import type { ProcedureItem } from './procedure-api.type'
import {
  DrawLineState,
  DrawMode,
  DrawingElementVisibleState,
  type EModelVersion,
  type CameraAnimationConfig,
  type DrawLineStateType,
  type DrawModeType,
  type DrawingElement,
  type ProcedureSliceType,
  type SceneType,
} from './procedure.type'
import {
  getLastVisibleUndoable,
  filterElementsForNewAddition,
  sortElementsByTimestamp,
} from 'features/Procedure/utils/drawingElements.utils'

export const ProcedureInitialState: ProcedureSliceType = {
  scene: {
    position: { x: 0.3510086203464686, y: 44.86891839345276, z: 22.64939590113562 },
    target: { x: -0.3788334723380865, y: 41.15888488936371, z: -5.529503427324806 },
  },
  desiredCameraPosition: null,
  drawingElements: [],
  selectedElement: null,
  drawMode: null,
  drawLineState: DrawLineState.IDLE,
  selectedEncounterUid: null,
  selectedComparisonEncounterUid: null,
  selectedPatientId: null,
  selectedProcedure: null,
  selectedLabId: null,
  selectedFormDocId: null,
  showElementIds: false,
  modelVersion: null,
  isPrinting: false,
  isModelSceneExpanded: true,
  isDrawCustomizerOpen: false,
  deletingProcedureId: null,
  isProcedureDeletePending: false,
}

const drawLinesArray = [DrawMode.FREE_LINE, DrawMode.STRAIGHT_LINE, DrawMode.MEASURE]

const clearDrawingInteractionState = (state: ProcedureSliceType) => {
  state.drawMode = null
  state.drawLineState = DrawLineState.IDLE
  state.isDrawCustomizerOpen = false
}

const createDrawingClientKey = () => nanoid()

const matchesDrawingIdentity = (element: DrawingElement, identity: string) => {
  return element.clientKey === identity || element.id === identity
}

export const procedureSlice = createSlice({
  name: REDUCERS.PROCEDURE,
  initialState: ProcedureInitialState,
  reducers: {
    resetState() {
      return ProcedureInitialState
    },
    updateSceneCoords(state, action: PayloadAction<SceneType>) {
      state.scene = action.payload
    },
    resetScene(state, action?: PayloadAction<{ exceptions?: (keyof ProcedureSliceType)[] }>) {
      const exceptions = action?.payload?.exceptions || []
      Object.keys(state).forEach((key) => {
        if (!exceptions.includes(key as keyof ProcedureSliceType)) {
          state[key] = ProcedureInitialState[key]
        }
      })
    },
    setSelectedElement(
      state,
      action: PayloadAction<{
        element: DrawingElement | null
        tooltipPosition?: Vector3Like
      }>,
    ) {
      if (action.payload.element === null) {
        state.selectedElement = null
      } else {
        state.selectedElement = {
          element: action.payload.element,
          tooltipPosition: action.payload.tooltipPosition,
        }
      }
    },
    setDrawMode(state, action: PayloadAction<DrawModeType>) {
      if (action.payload && !state.selectedProcedure) {
        clearDrawingInteractionState(state)
        return
      }

      state.drawMode = action.payload
      if (!drawLinesArray.includes(action.payload)) {
        state.drawLineState = DrawLineState.IDLE
      }
      if (!action.payload) {
        state.isDrawCustomizerOpen = false
      }
    },
    setDrawLineState(state, action: PayloadAction<DrawLineStateType>) {
      state.drawLineState = action.payload
    },
    setDesiredCameraPosition(state, action: PayloadAction<CameraAnimationConfig | null>) {
      state.desiredCameraPosition = action.payload
    },
    addDrawingElement(
      state,
      action: PayloadAction<Omit<DrawingElement, 'visibleState' | 'timestamp'>>,
    ) {
      const element = action.payload

      const lastVisibleUndoable = getLastVisibleUndoable(state.drawingElements)

      const newElements = filterElementsForNewAddition(state.drawingElements, lastVisibleUndoable)

      newElements.push({
        ...element,
        clientKey: createDrawingClientKey(),
        visibleState: DrawingElementVisibleState.VISIBLE,
        timestamp: Date.now(),
        isModified: true,
      })

      state.drawingElements = sortElementsByTimestamp(newElements)
    },
    setDrawingElements(state, action: PayloadAction<DrawingElement[]>) {
      state.drawingElements = action.payload
    },
    removeProcedureDrawingElements(state, action: PayloadAction<number>) {
      const procedureId = action.payload

      state.drawingElements = state.drawingElements.filter((el) => el.procedureId !== procedureId)

      if (state.selectedElement?.element.procedureId === procedureId) {
        state.selectedElement = null
      }
    },
    updateDrawingElement(
      state,
      action: PayloadAction<{ id: string; updates: Partial<DrawingElement> }>,
    ) {
      const element = state.drawingElements.find((el) =>
        matchesDrawingIdentity(el, action.payload.id),
      )
      if (element) {
        Object.assign(element, action.payload.updates)
        // Mark elements as modified when updated so they get saved
        if (
          element.visibleState === DrawingElementVisibleState.PERSISTED ||
          element.visibleState === DrawingElementVisibleState.VISIBLE
        ) {
          element.isModified = true
        }
        if (
          state.selectedElement?.element.clientKey === action.payload.id ||
          state.selectedElement?.element.id === action.payload.id
        ) {
          Object.assign(state.selectedElement.element, action.payload.updates)
        }
      }
    },
    setSelectedEncounterUid(state, action: PayloadAction<string | null>) {
      if (state.selectedEncounterUid !== action.payload) {
        state.modelVersion = null
      }
      state.selectedEncounterUid = action.payload
    },
    setSelectedComparisonEncounterUid(state, action: PayloadAction<string | null>) {
      state.selectedComparisonEncounterUid = action.payload
    },
    setSelectedPatientId(state, action: PayloadAction<number | null>) {
      state.selectedPatientId = action.payload
    },
    setSelectedProcedure(state, action: PayloadAction<ProcedureItem | null>) {
      state.selectedProcedure = action.payload
      state.selectedElement = null
      if (!action.payload) {
        clearDrawingInteractionState(state)
      }
    },
    setSelectedLabId(state, action: PayloadAction<number | null>) {
      state.selectedLabId = action.payload
    },
    setSelectedFormDocId(state, action: PayloadAction<number | null>) {
      state.selectedFormDocId = action.payload
    },
    mergeProcedureDrawingElements(state, action: PayloadAction<DrawingElement[]>) {
      const existingClientKeys = new Map(
        state.drawingElements.map((element) => [
          `${element.procedureId ?? 'unknown'}:${element.id}`,
          element.clientKey,
        ]),
      )

      state.drawingElements = action.payload.map((element) => ({
        ...element,
        clientKey:
          element.clientKey ||
          existingClientKeys.get(`${element.procedureId ?? 'unknown'}:${element.id}`) ||
          createDrawingClientKey(),
      }))
    },
    toggleShowElementIds(state) {
      state.showElementIds = !state.showElementIds
    },
    setShowElementIds(state, action: PayloadAction<boolean>) {
      state.showElementIds = action.payload
    },
    toggleModelSceneExpanded(state) {
      state.isModelSceneExpanded = !state.isModelSceneExpanded
    },
    setModelSceneExpanded(state, action: PayloadAction<boolean>) {
      state.isModelSceneExpanded = action.payload
    },
    setDrawCustomizerOpen(state, action: PayloadAction<boolean>) {
      state.isDrawCustomizerOpen = Boolean(
        action.payload && state.selectedProcedure && state.drawMode,
      )
    },
    setProcedureDeletePending(state, action: PayloadAction<number | null>) {
      state.deletingProcedureId = action.payload
      state.isProcedureDeletePending = action.payload !== null
    },
    setModelVersion(state, action: PayloadAction<EModelVersion>) {
      state.modelVersion = action.payload
    },
    setIsPrinting(state, action: PayloadAction<boolean>) {
      state.isPrinting = action.payload
    },
    deleteDrawingElement(state, action: PayloadAction<string>) {
      const elementToDelete = state.drawingElements.find((el) =>
        matchesDrawingIdentity(el, action.payload),
      )
      if (!elementToDelete) return

      const procedureId = elementToDelete.procedureId
      const procedurePosition = elementToDelete.id.split('-')[0]
      const deletedSuffix = Number.parseInt(elementToDelete.id.split('-')[1] || '', 10)

      state.drawingElements = state.drawingElements.filter(
        (el) => !matchesDrawingIdentity(el, action.payload),
      )

      if (procedureId && !Number.isNaN(deletedSuffix)) {
        state.drawingElements.forEach((element) => {
          if (element.procedureId !== procedureId) return

          const [elementProcedurePosition, rawSuffix] = element.id.split('-')
          const elementSuffix = Number.parseInt(rawSuffix || '', 10)

          if (elementProcedurePosition !== procedurePosition || Number.isNaN(elementSuffix)) return
          if (elementSuffix <= deletedSuffix) return

          const oldId = element.id
          element.id = `${procedurePosition}-${elementSuffix - 1}`
          element.isModified = true

          if (
            state.selectedElement?.element.clientKey === element.clientKey ||
            state.selectedElement?.element.id === oldId
          ) {
            state.selectedElement.element.id = element.id
          }
        })
      }

      if (
        state.selectedElement?.element.clientKey === action.payload ||
        state.selectedElement?.element.id === action.payload
      ) {
        state.selectedElement = null
      }
    },
    clearDrawingHistory(state) {
      state.drawingElements = state.drawingElements.filter(
        (el) => el.visibleState !== DrawingElementVisibleState.HIDDEN,
      )
    },

    resetSceneSensitiveData(state) {
      state.selectedElement = null
      state.selectedEncounterUid = null
      state.selectedComparisonEncounterUid = null
      state.selectedPatientId = null
      state.selectedProcedure = null
      state.deletingProcedureId = null
      state.isProcedureDeletePending = false
      clearDrawingInteractionState(state)
    },
  },
})

export const {
  updateSceneCoords,
  resetScene,
  setSelectedElement,
  setDrawMode,
  setDrawLineState,
  setDesiredCameraPosition,
  addDrawingElement,
  setDrawingElements,
  removeProcedureDrawingElements,
  updateDrawingElement,
  setSelectedEncounterUid,
  setSelectedComparisonEncounterUid,
  setSelectedPatientId,
  setSelectedProcedure,
  setSelectedLabId,
  setSelectedFormDocId,
  mergeProcedureDrawingElements,
  toggleShowElementIds,
  setShowElementIds,
  deleteDrawingElement,
  setModelVersion,
  setIsPrinting,
  clearDrawingHistory,
  resetState,
  resetSceneSensitiveData,
  setModelSceneExpanded,
  setDrawCustomizerOpen,
  setProcedureDeletePending,
} = procedureSlice.actions
